/**
 * Admin Dashboard — internal ops console for the 5.25 微信内测.
 *
 * Routes: `/admin` is the entry point. Bypasses the normal product flow entirely.
 *
 * Auth model:
 *   - Same Magic Link cookie as regular users + ADMIN_EMAILS allowlist.
 *   - No separate admin password.
 *   - On mount we hit /api/admin/me. Possible outcomes:
 *       2xx { email }  → render dashboard
 *       401 no_session → "未登录" screen with a button to /login
 *       403 forbidden  → "这个邮箱不在 admin 名单" screen
 *       503 disabled   → "管理员功能未配置" screen (ADMIN_EMAILS env missing)
 *
 * Five tabs:
 *   1. 用户       — list of users + derived 启动成功率 (no edit — no manual fields)
 *   2. 反馈流     — round_experiences flat list with派生信号 chips (有帮助 / 知道下一步)
 *   3. 卡片       — all generated cards with origin_wish
 *   4. 邀请码     — invite_codes list + generate form + revoke buttons + copy templates
 *   5. 愿望流     — segments grouped by wish text
 *
 * Design rule: every field surfaced here populates AUTOMATICALLY from the product
 * flow. No nickname / contact / "ops notes" — users are identified by email and
 * source_channel; that's all the system actually knows about them. Derived
 * signals (有帮助 / 知道下一步) are computed at render time from user_status and
 * next_start length — we never asked the user new questions to surface them.
 */
import { useEffect, useMemo, useState } from 'react';

type TabKey = 'users' | 'feedback' | 'cards' | 'invites' | 'wishes';

interface UserRow {
  id: string;
  email: string;
  source_channel: string | null;
  invited_by: string | null;
  created_at: number;
  last_active_at: number | null;
  segments_count: number;
  segments_completed: number;
  feedback_count: number;
  helpful_count: number;
}

interface FeedbackRow {
  id: string;
  return_point: string | null;
  user_status: 'done' | 'in_progress' | 'pending' | 'drop';
  trace: string;
  stuck_point: string | null;
  next_start: string;
  support: string;
  created_at: number;
  round_no: number;
  card_id: string;
  segment_id: string;
  wish: string;
  user_id: string;
  email: string;
  source_channel: string | null;
}

interface CardRow {
  id: string;
  user_id: string;
  title: string;
  action: string;
  status: string;
  age: number;
  origin_segment_id: string | null;
  origin_wish: string | null;
  created_at: number;
  updated_at: number;
  email: string;
  source_channel: string | null;
}

interface InviteRow {
  code: string;
  source: string;
  max_uses: number;
  uses: number;
  expires_at: number | null;
  activated_user_id: string | null;
  created_at: number;
  activated_email: string | null;
}

interface WishRow {
  id: string;
  wish: string;
  started_at: number;
  completed_at: number | null;
  current_round: number;
  email: string;
  source_channel: string | null;
  cards_count: number;
}

// Admin endpoints rely on the same gime5_session cookie regular users have, so
// we just send credentials and let the server check the ADMIN_EMAILS allowlist.
// Distinct error kinds let the SPA show the right "why you can't see this" screen.
async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers ?? {}),
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (res.status === 403) throw new Error('forbidden');
  if (res.status === 503) throw new Error('admin_disabled');
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mi}`;
}

function fmtPercent(num: number, den: number): string {
  if (den === 0) return '—';
  return `${Math.round((num / den) * 100)}%`;
}

// Derived "有帮助" signal: user marked done → 有帮助, in_progress → 中性, drop/pending → 没帮助.
function derivedHelpedLabel(status: FeedbackRow['user_status']): { label: string; color: string } {
  if (status === 'done') return { label: '有帮助', color: '#1f7a56' };
  if (status === 'in_progress') return { label: '中性', color: '#a66c13' };
  return { label: '没帮助', color: '#a34235' };
}

// Derived "知道下一步" signal: filled next_start with > 5 chars = knows; otherwise vague.
function derivedKnowsNext(nextStart: string): { label: string; color: string } {
  const trimmed = nextStart.trim();
  if (trimmed.length > 5) return { label: '知道下一步', color: '#1f7a56' };
  return { label: '仍模糊', color: '#a34235' };
}

type AuthState =
  | { kind: 'loading' }
  | { kind: 'authed'; email: string }
  | { kind: 'unauthed' }
  | { kind: 'forbidden' }
  | { kind: 'disabled' }
  | { kind: 'error'; detail: string };

export function Admin() {
  const [auth, setAuth] = useState<AuthState>({ kind: 'loading' });
  const [activeTab, setActiveTab] = useState<TabKey>('users');

  useEffect(() => {
    let cancelled = false;
    adminFetch<{ ok: boolean; email: string }>('/api/admin/me')
      .then((d) => {
        if (cancelled) return;
        setAuth({ kind: 'authed', email: d.email });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (err.message === 'unauthorized') setAuth({ kind: 'unauthed' });
        else if (err.message === 'forbidden') setAuth({ kind: 'forbidden' });
        else if (err.message === 'admin_disabled') setAuth({ kind: 'disabled' });
        else setAuth({ kind: 'error', detail: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.kind === 'loading') {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#66716d' }}>正在检查权限…</p>
      </div>
    );
  }

  if (auth.kind === 'unauthed') {
    return (
      <div style={containerStyle}>
        <h1 style={{ marginTop: 0 }}>需要先登录</h1>
        <p style={{ color: '#66716d' }}>
          Admin 控制台用的就是你平时登录的那个邮箱。先去登录,登完用同一个邮箱回来这里。
        </p>
        <a href="/login" style={primaryLinkStyle}>去登录 →</a>
      </div>
    );
  }

  if (auth.kind === 'forbidden') {
    return (
      <div style={containerStyle}>
        <h1 style={{ marginTop: 0 }}>这个邮箱不在 admin 名单</h1>
        <p style={{ color: '#66716d' }}>
          你当前登录的邮箱不是管理员。如果你确实是管理员,可能登错了账号——退出后用 admin 邮箱重新登。
        </p>
        <button type="button" onClick={() => { void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).finally(() => { window.location.href = '/login'; }); }} style={primaryLinkStyle}>
          退出当前账号,重新登录
        </button>
      </div>
    );
  }

  if (auth.kind === 'disabled') {
    return (
      <div style={containerStyle}>
        <h1 style={{ marginTop: 0 }}>管理员功能未配置</h1>
        <p style={{ color: '#66716d' }}>
          worker 没有设置 ADMIN_EMAILS 环境变量。生产环境请用 <code>wrangler secret put ADMIN_EMAILS</code>;本地开发请编辑 <code>apps/worker/.dev.vars</code>。
        </p>
      </div>
    );
  }

  if (auth.kind === 'error') {
    return (
      <div style={containerStyle}>
        <h1 style={{ marginTop: 0, color: '#a34235' }}>出错了</h1>
        <p>{auth.detail}</p>
      </div>
    );
  }

  // authed
  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Gime5 Admin · 内测控制台</h1>
        <span style={{ marginLeft: 'auto', color: '#66716d', fontSize: 13 }}>登录为 {auth.email}</span>
      </header>

      <nav style={tabBarStyle}>
        {(
          [
            ['users', '用户'],
            ['feedback', '反馈流'],
            ['cards', '卡片'],
            ['invites', '邀请码'],
            ['wishes', '愿望流'],
          ] as [TabKey, string][]
        ).map(([k, label]) => (
          <button
            type="button"
            key={k}
            onClick={() => setActiveTab(k)}
            style={tabBtn(activeTab === k)}
          >
            {label}
          </button>
        ))}
      </nav>

      <section>
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'feedback' && <FeedbackTab />}
        {activeTab === 'cards' && <CardsTab />}
        {activeTab === 'invites' && <InvitesTab />}
        {activeTab === 'wishes' && <WishesTab />}
      </section>
    </div>
  );
}

// Once the Admin shell has verified auth via /me, individual tab queries don't
// need to handle 401/403 specially — if those happen mid-session (e.g. cookie
// expired), surfacing the raw error in the tab is fine. The shell would also
// surface the same problem on next refresh.
function useAdminQuery<T>(path: string): {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminFetch<T>(path)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, tick]);

  return { data, loading, error, refresh: () => setTick((t) => t + 1) };
}

// ============================================================================
// Tab 1: Users
// ============================================================================
function UsersTab() {
  const { data, loading, error, refresh } = useAdminQuery<{ ok: boolean; users: UserRow[] }>(
    '/api/admin/users',
  );

  const onDelete = async (id: string, email: string) => {
    if (!confirm(`确认删除 ${email}?所有卡片/段落/反馈都会一并清掉,不可恢复。`)) return;
    try {
      await adminFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
      refresh();
    } catch (err) {
      alert(`删除失败: ${(err as Error).message}`);
    }
  };

  if (loading) return <p>加载中…</p>;
  if (error) return <p style={{ color: '#a34235' }}>错误: {error}</p>;
  const users = data?.users ?? [];

  return (
    <div>
      <p style={{ color: '#66716d' }}>
        共 {users.length} 个用户。所有字段都是产品流程自动捕获的——没有人工备注。
      </p>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>邮箱</th>
            <th style={thStyle}>来源渠道</th>
            <th style={thStyle}>邀请码</th>
            <th style={thStyle}>注册</th>
            <th style={thStyle}>最近活跃</th>
            <th style={thStyle}>段落 (完成/总)</th>
            <th style={thStyle}>反馈数</th>
            <th style={thStyle}>启动成功率</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td style={tdStyle}>{u.email}</td>
              <td style={tdStyle}>{u.source_channel ?? '—'}</td>
              <td style={tdStyle}>{u.invited_by ?? '—'}</td>
              <td style={tdStyle}>{fmtDate(u.created_at)}</td>
              <td style={tdStyle}>{fmtDate(u.last_active_at)}</td>
              <td style={tdStyle}>
                {u.segments_completed} / {u.segments_count}
              </td>
              <td style={tdStyle}>{u.feedback_count}</td>
              <td style={tdStyle}>{fmtPercent(u.helpful_count, u.feedback_count)}</td>
              <td style={tdStyle}>
                <button type="button" onClick={() => onDelete(u.id, u.email)} style={dangerBtnStyle}>
                  删除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Tab 2: Feedback flow
// ============================================================================
function FeedbackTab() {
  const { data, loading, error } = useAdminQuery<{ ok: boolean; feedback: FeedbackRow[] }>(
    '/api/admin/feedback?limit=300',
  );
  const [filterChannel, setFilterChannel] = useState('');
  const [filterHelped, setFilterHelped] = useState<'all' | 'done' | 'in_progress' | 'drop'>('all');

  const feedback = data?.feedback ?? [];
  const channels = useMemo(
    () => Array.from(new Set(feedback.map((f) => f.source_channel).filter(Boolean))) as string[],
    [feedback],
  );
  const filtered = feedback.filter((f) => {
    if (filterChannel && f.source_channel !== filterChannel) return false;
    if (filterHelped !== 'all' && f.user_status !== filterHelped) return false;
    return true;
  });

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const todayCount = feedback.filter((f) => f.created_at >= todayMs).length;
    const doneRate = feedback.length
      ? Math.round((feedback.filter((f) => f.user_status === 'done').length / feedback.length) * 100)
      : 0;
    return { total: feedback.length, today: todayCount, doneRate };
  }, [feedback]);

  if (loading) return <p>加载中…</p>;
  if (error) return <p style={{ color: '#a34235' }}>错误: {error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 24, padding: '12px 16px', background: '#fffdf8', border: '1px solid #ddd8cc', borderRadius: 6, marginBottom: 16 }}>
        <span><strong>今日反馈:</strong> {stats.today}</span>
        <span><strong>累计反馈:</strong> {stats.total}</span>
        <span><strong>启动成功率 (done):</strong> {stats.doneRate}%</span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <label>
          来源:{' '}
          <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
            <option value="">全部</option>
            {channels.map((ch) => (
              <option key={ch} value={ch}>
                {ch}
              </option>
            ))}
          </select>
        </label>
        <label>
          状态:{' '}
          <select value={filterHelped} onChange={(e) => setFilterHelped(e.target.value as typeof filterHelped)}>
            <option value="all">全部</option>
            <option value="done">有帮助 (done)</option>
            <option value="in_progress">中性 (in_progress)</option>
            <option value="drop">没帮助 (drop)</option>
          </select>
        </label>
      </div>

      {filtered.map((f) => {
        const helped = derivedHelpedLabel(f.user_status);
        const knows = derivedKnowsNext(f.next_start);
        return (
          <article key={f.id} style={cardStyle}>
            <header style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <strong>{f.email}</strong>
              {f.source_channel && <span style={chipStyle('#a66c13')}>{f.source_channel}</span>}
              <span style={chipStyle(helped.color)}>{helped.label}</span>
              <span style={chipStyle(knows.color)}>{knows.label}</span>
              <span style={{ marginLeft: 'auto', color: '#66716d', fontSize: 13 }}>
                {fmtDate(f.created_at)} · 第{f.round_no}轮
              </span>
            </header>
            <p style={{ margin: '4px 0', color: '#34403d' }}>
              <strong>愿望:</strong> {f.wish.length > 100 ? f.wish.slice(0, 100) + '…' : f.wish}
            </p>
            <p style={{ margin: '4px 0' }}>
              <strong>痕迹:</strong> {f.trace}
            </p>
            {f.stuck_point && (
              <p style={{ margin: '4px 0', color: '#a34235' }}>
                <strong>卡住:</strong> {f.stuck_point}
              </p>
            )}
            <p style={{ margin: '4px 0' }}>
              <strong>下次从:</strong> {f.next_start}
            </p>
            <p style={{ margin: '4px 0', color: '#1f7a56' }}>
              <strong>支撑:</strong> {f.support}
            </p>
          </article>
        );
      })}
      {filtered.length === 0 && <p style={{ color: '#66716d' }}>没有匹配的反馈。</p>}
    </div>
  );
}

// ============================================================================
// Tab 3: Cards
// ============================================================================
function CardsTab() {
  const { data, loading, error } = useAdminQuery<{ ok: boolean; cards: CardRow[] }>(
    '/api/admin/cards?limit=300',
  );

  if (loading) return <p>加载中…</p>;
  if (error) return <p style={{ color: '#a34235' }}>错误: {error}</p>;
  const cards = data?.cards ?? [];

  return (
    <div>
      <p style={{ color: '#66716d' }}>共 {cards.length} 张卡 (最多 300)。</p>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>用户</th>
            <th style={thStyle}>title</th>
            <th style={thStyle}>action</th>
            <th style={thStyle}>origin_wish</th>
            <th style={thStyle}>状态</th>
            <th style={thStyle}>age</th>
            <th style={thStyle}>来源</th>
            <th style={thStyle}>创建</th>
          </tr>
        </thead>
        <tbody>
          {cards.map((cd) => (
            <tr key={cd.id}>
              <td style={tdStyle}>{cd.email}</td>
              <td style={tdStyle}>{cd.title}</td>
              <td style={tdStyle}>{cd.action}</td>
              <td style={tdStyle}>{cd.origin_wish ? (cd.origin_wish.length > 80 ? cd.origin_wish.slice(0, 80) + '…' : cd.origin_wish) : '—'}</td>
              <td style={tdStyle}>{cd.status}</td>
              <td style={tdStyle}>{cd.age}</td>
              <td style={tdStyle}>{cd.source_channel ?? '—'}</td>
              <td style={tdStyle}>{fmtDate(cd.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Tab 4: Invites
// ============================================================================
function InvitesTab() {
  const { data, loading, error, refresh } = useAdminQuery<{ ok: boolean; invites: InviteRow[] }>(
    '/api/admin/invites',
  );

  const [source, setSource] = useState('朋友圈');
  const [count, setCount] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInHours, setExpiresInHours] = useState<number | ''>(48);
  const [generated, setGenerated] = useState<{ code: string }[]>([]);
  const [genError, setGenError] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);

  const onGenerate = async () => {
    setGenLoading(true);
    setGenError(null);
    setGenerated([]);
    try {
      const resp = await adminFetch<{ ok: boolean; created: { code: string }[] }>(
        '/api/admin/invites',
        {
          method: 'POST',
          body: JSON.stringify({
            source,
            count,
            maxUses,
            expiresInMs: expiresInHours === '' ? null : expiresInHours * 60 * 60 * 1000,
          }),
        },
      );
      setGenerated(resp.created);
      refresh();
    } catch (err) {
      setGenError((err as Error).message);
    } finally {
      setGenLoading(false);
    }
  };

  const onRevoke = async (code: string) => {
    if (!confirm(`确认 revoke ${code}? 已激活的用户不受影响,但未激活的链接立即失效。`)) return;
    try {
      await adminFetch(`/api/admin/invites/${encodeURIComponent(code)}/revoke`, { method: 'POST' });
      refresh();
    } catch (err) {
      alert(`revoke 失败: ${(err as Error).message}`);
    }
  };

  const onCopyTemplate = (code: string) => {
    const text = `想试一个 5 分钟启动卡片的小工具吗？打开 https://starter.cyberunion.net 填邀请码 ${code} 就可以进。15 分钟邮件链接有效。有反馈直接戳我就好。`;
    navigator.clipboard.writeText(text).catch(() => alert('复制失败:' + text));
  };

  if (loading) return <p>加载中…</p>;
  if (error) return <p style={{ color: '#a34235' }}>错误: {error}</p>;
  const invites = data?.invites ?? [];

  return (
    <div>
      {/* Generate form */}
      <section style={{ border: '1px solid #ddd8cc', borderRadius: 6, padding: 16, marginBottom: 24, background: '#fffdf8' }}>
        <h3 style={{ marginTop: 0 }}>生成邀请码</h3>
        <p style={{ color: '#66716d', marginTop: 0, fontSize: 13 }}>
          来源 = 这批码要发到的渠道。无法自动捕获,必须由运营填写——后续归因都靠它。
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
          <label>
            来源
            <input value={source} onChange={(e) => setSource(e.target.value)} list="source-presets" style={inputStyle} />
            <datalist id="source-presets">
              <option value="朋友圈" />
              <option value="视频号" />
              <option value="公众号" />
              <option value="私聊" />
              <option value="微信群" />
              <option value="线下" />
              <option value="转介绍" />
            </datalist>
          </label>
          <label>
            数量
            <input type="number" min={1} max={50} value={count} onChange={(e) => setCount(Number(e.target.value) || 1)} style={inputStyle} />
          </label>
          <label>
            每码可用次数
            <input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value) || 1)} style={inputStyle} />
          </label>
          <label>
            有效期 (小时,空=永久)
            <input
              type="number"
              min={1}
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(e.target.value === '' ? '' : Number(e.target.value))}
              style={inputStyle}
            />
          </label>
          <button type="button" onClick={onGenerate} disabled={genLoading} style={{ ...primaryBtnStyle, gridColumn: '1 / 5' }}>
            {genLoading ? '生成中…' : '生成'}
          </button>
        </div>
        {genError && <p style={{ color: '#a34235' }}>错误: {genError}</p>}
        {generated.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#e1edf4', borderRadius: 6 }}>
            <strong>刚生成 {generated.length} 个码:</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontFamily: 'monospace' }}>
              {generated.map((g) => (
                <li key={g.code} style={{ marginBottom: 4 }}>
                  {g.code}{' '}
                  <button type="button" onClick={() => onCopyTemplate(g.code)} style={tinyBtnStyle}>
                    复制话术
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Invite list */}
      <h3>所有邀请码 ({invites.length})</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>code</th>
            <th style={thStyle}>来源</th>
            <th style={thStyle}>uses / max</th>
            <th style={thStyle}>过期</th>
            <th style={thStyle}>激活给</th>
            <th style={thStyle}>创建</th>
            <th style={thStyle}>状态</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {invites.map((i) => {
            const now = Date.now();
            const expired = i.expires_at !== null && i.expires_at < now;
            const exhausted = i.uses >= i.max_uses;
            const statusLabel = expired ? '过期/revoke' : exhausted ? '耗尽' : i.uses > 0 ? '部分用' : '未用';
            const statusColor = expired ? '#a34235' : exhausted ? '#a66c13' : i.uses > 0 ? '#245c83' : '#1f7a56';
            return (
              <tr key={i.code} style={expired || exhausted ? { opacity: 0.55 } : undefined}>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{i.code}</td>
                <td style={tdStyle}>{i.source}</td>
                <td style={tdStyle}>
                  {i.uses} / {i.max_uses}
                </td>
                <td style={tdStyle}>{i.expires_at ? fmtDate(i.expires_at) : '永久'}</td>
                <td style={tdStyle}>{i.activated_email ?? '—'}</td>
                <td style={tdStyle}>{fmtDate(i.created_at)}</td>
                <td style={tdStyle}>
                  <span style={chipStyle(statusColor)}>{statusLabel}</span>
                </td>
                <td style={tdStyle}>
                  <button type="button" onClick={() => onCopyTemplate(i.code)} style={tinyBtnStyle}>
                    复制话术
                  </button>
                  {!expired && (
                    <button type="button" onClick={() => onRevoke(i.code)} style={{ ...dangerBtnStyle, marginLeft: 6 }}>
                      revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Tab 5: Wishes
// ============================================================================
function WishesTab() {
  const { data, loading, error } = useAdminQuery<{ ok: boolean; wishes: WishRow[] }>(
    '/api/admin/wishes',
  );

  if (loading) return <p>加载中…</p>;
  if (error) return <p style={{ color: '#a34235' }}>错误: {error}</p>;
  const wishes = data?.wishes ?? [];

  return (
    <div>
      <p style={{ color: '#66716d' }}>所有 segment.wish (按时间倒序,最多 200)。</p>
      {wishes.map((w) => (
        <article key={w.id} style={cardStyle}>
          <header style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <strong>{w.email}</strong>
            {w.source_channel && <span style={chipStyle('#a66c13')}>{w.source_channel}</span>}
            <span style={{ marginLeft: 'auto', color: '#66716d', fontSize: 13 }}>
              {fmtDate(w.started_at)} · {w.completed_at ? `完成第${w.current_round}轮` : `进行到第${w.current_round}轮`} · {w.cards_count} 张卡
            </span>
          </header>
          <p style={{ margin: 0 }}>{w.wish}</p>
        </article>
      ))}
      {wishes.length === 0 && <p style={{ color: '#66716d' }}>还没有 wish 数据。</p>}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================
const containerStyle: React.CSSProperties = {
  maxWidth: 1280,
  margin: '0 auto',
  padding: '24px 20px 60px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif',
  fontSize: 14,
  color: '#18211f',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 16,
};

const tabBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  borderBottom: '1px solid #ddd8cc',
  marginBottom: 20,
};

const tabBtn = (active: boolean): React.CSSProperties => ({
  padding: '10px 16px',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid #1f7a56' : '2px solid transparent',
  color: active ? '#1f7a56' : '#66716d',
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  fontSize: 15,
});

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: 8,
  textAlign: 'left',
  borderBottom: '2px solid #ddd8cc',
  background: '#f1ede3',
  fontSize: 12,
  textTransform: 'uppercase',
  color: '#52605b',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: '1px solid #ece8de',
  verticalAlign: 'top',
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #ddd8cc',
  borderRadius: 6,
  padding: 14,
  marginBottom: 10,
  background: '#fffdf8',
};

const chipStyle = (color: string): React.CSSProperties => ({
  display: 'inline-flex',
  padding: '3px 8px',
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 12,
  background: color + '22',
  color,
});

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  marginTop: 4,
  fontSize: 14,
  border: '1px solid #ddd8cc',
  borderRadius: 4,
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1f7a56',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const primaryLinkStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '10px 18px',
  background: '#1f7a56',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  marginTop: 8,
};

const dangerBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  background: '#fff',
  color: '#a34235',
  border: '1px solid #a34235',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
};

const tinyBtnStyle: React.CSSProperties = {
  padding: '3px 8px',
  fontSize: 11,
  background: '#fff',
  border: '1px solid #ddd8cc',
  borderRadius: 4,
  cursor: 'pointer',
};
